import { useEffect, useState } from "react";
import { api } from "@/api/client";
import { CalendarClock, Users, Film } from "lucide-react";

export default function Meetings() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    api.get("/v1/meetings/").then((r) => setItems(r.data.items || [])).catch((e) => {
      if (e?.response?.status === 403) setDenied(true);
    }).finally(() => setLoading(false));
  }, []);

  if (denied) {
    return (
      <div className="p-8" data-testid="meetings-access-denied">
        <div className="rounded-lg border border-[#E7E5E4] bg-[#FFFFFF] p-8 text-center">
          <h1 className="font-editorial text-2xl tracking-tight mb-2">Access Restricted</h1>
          <p className="text-sm text-[#57534E]">Your role doesn't have permission to view Meetings.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6" data-testid="meetings-page">
      <div>
        <h1 className="font-editorial text-4xl tracking-tight">Meetings</h1>
        <p className="text-sm text-[#57534E] mt-1">Chronological timeline of client & project meetings. Read-first — proposing edits arrives in a later cut.</p>
      </div>

      {loading ? (
        <div className="text-sm text-[#A8A29E]">Loading…</div>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-[#E7E5E4] bg-[#FFFFFF] p-8 text-center text-sm text-[#A8A29E]" data-testid="meetings-empty">
          No meetings synced yet — once entries land in the Meetings &amp; Notes Notion database they'll appear here chronologically.
        </div>
      ) : (
        <div className="relative pl-6 space-y-4 before:absolute before:left-[7px] before:top-2 before:bottom-2 before:w-px before:bg-[#E7E5E4]">
          {items.map((m) => (
            <div key={m.id} data-testid={`meeting-row-${m.id}`} className="relative">
              <div className="absolute -left-6 top-1.5 h-3 w-3 rounded-full bg-[#FFC817] ring-4 ring-[#FFFFFF]" />
              <button
                onClick={() => setSelected(selected === m.id ? null : m.id)}
                data-testid={`meeting-expand-${m.id}`}
                className="w-full text-left rounded-lg border border-[#E7E5E4] bg-[#FFFFFF] p-4 hover:border-[#D6D3D1]"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 text-sm text-[#1C1C1E] font-medium">
                      <CalendarClock className="h-3.5 w-3.5 text-[#B45309] flex-shrink-0" /> {m.name}
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5 mt-2">
                      {m.type && <span className="rounded-full bg-[#E7E5E4] px-2 py-0.5 text-[10px] uppercase tracking-wider text-[#57534E]">{m.type}</span>}
                      {(m.client_names || []).map((n) => (
                        <span key={n} className="inline-flex items-center gap-1 rounded-full bg-[#FFC817]/10 border border-[#FFC817]/30 px-2 py-0.5 text-[10px] text-[#B45309]"><Users className="h-2.5 w-2.5" />{n}</span>
                      ))}
                      {(m.project_names || []).map((n) => (
                        <span key={n} className="inline-flex items-center gap-1 rounded-full bg-[#E7E5E4] px-2 py-0.5 text-[10px] text-[#57534E]"><Film className="h-2.5 w-2.5" />{n}</span>
                      ))}
                    </div>
                  </div>
                  <div className="text-[10px] font-mono text-[#A8A29E] flex-shrink-0">{m.date?.slice(0, 10)}</div>
                </div>
                {selected === m.id && (
                  <div className="mt-3 pt-3 border-t border-[#E7E5E4] space-y-2 text-xs">
                    {m.decisions && <div><span className="text-[#A8A29E] uppercase tracking-wider text-[10px]">Decisions</span><p className="text-[#57534E] mt-0.5">{m.decisions}</p></div>}
                    {m.action_items && <div><span className="text-[#A8A29E] uppercase tracking-wider text-[10px]">Action Items</span><p className="text-[#57534E] mt-0.5">{m.action_items}</p></div>}
                  </div>
                )}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
